import React from 'react';
import { Document } from '@contentful/rich-text-types';

import { formatToPhone } from '../../../utils/formatPhoneNumber';

// Form Fields
import { TextInput } from '../../FormFields/TextInput';
import { Date } from '../../FormFields/Date';
import { TextAreaInput } from '../../FormFields/TextAreaInput';
import { Select } from '../../FormFields/Select';
import { Checkbox } from '../../FormFields/Checkbox';

import { TextInputType } from '../../FormFields/shared';
import { formFieldEnum } from './shared';

interface FormFieldProps {
  id: string;
  type: formFieldEnum;
  label: string;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  options?: any[];
  optionType?: string;
  disclaimer?: Document | null;
  formGroupId?: string;
  state: any;
  errors: any;
  handleOnChange: (key: string, val: any) => void;
}

export function FormField({
  id,
  type,
  formGroupId,
  label,
  placeholder,
  required,
  maxLength,
  state,
  errors,
  handleOnChange,
  ...fields
}: FormFieldProps) {
  switch (type) {
    case formFieldEnum.TEXT:
    case formFieldEnum.EMAIL:
    case formFieldEnum.NUMBER:
      let inputType = TextInputType.TEXT;

      if (type === formFieldEnum.EMAIL) {
        inputType = TextInputType.EMAIL;
      } else if (type === formFieldEnum.NUMBER) {
        inputType = TextInputType.NUMBER;
      }

      return (
        <TextInput
          type={inputType}
          label={label}
          placeholder={placeholder}
          value={state[id]}
          error={errors[id]}
          required={required}
          maxLength={maxLength}
          handleOnChange={(e, val: string) => handleOnChange(id, val)}
        />
      );

    case formFieldEnum.PHONE:
      return (
        <TextInput
          type={TextInputType.PHONE}
          label={label}
          placeholder={placeholder}
          value={state[id]}
          error={errors[id]}
          required={required}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={14}
          handleOnChange={(e, val: string) =>
            handleOnChange(id, formatToPhone(val) ?? '')
          }
        />
      );

    case formFieldEnum.DATE:
      return (
        <Date
          type={TextInputType.DATE}
          label={label}
          value={state[id]}
          error={errors[id]}
          required={required}
          handleOnChange={(e, val: any) => handleOnChange(id, val)}
        />
      );

    case formFieldEnum.TEXTAREA:
      return (
        <TextAreaInput
          label={label}
          placeholder={placeholder}
          value={state[id]}
          maxLength={maxLength || 140}
          handleOnChange={(e, message: string) => handleOnChange(id, message)}
        />
      );

    case formFieldEnum.SELECT:
      const options = (fields?.options || []).map((opt: string) => ({
        key: opt,
        value: opt
      }));

      return (
        <Select
          type={TextInputType.TEXT}
          label={label}
          options={[{ key: ' ', value: ' ' }, ...options]}
          value={state[id]}
          error={errors[id]}
          required={required}
          handleOnChange={({ target }: any) =>
            handleOnChange(id, target.value ?? '')
          }
        />
      );

    case formFieldEnum.RADIO:
    case formFieldEnum.CHECKBOX:
      return (
        <Checkbox
          type={type}
          name={formGroupId ?? 'dynamic-form-checkbox'}
          label={label}
          value={state[id]}
          checked={state[id] && state[id] !== ''}
          error={errors[id]}
          required={required}
          disclaimer={fields?.disclaimer}
          handleOnChange={(e: any, val: any) => handleOnChange(id, val)}
        />
      );

    default:
      return null;
  }
}
